/**
 * Tackle.au — whitelabel fishing storefront powered by the same DSZ
 * catalog as HustlePlaza.  Filters products with hp_storefront="tackle"
 * (set by /app/scripts/categorise_fishing.py) and re-skins the shop in
 * a marine/deep-water aesthetic.
 *
 * Architecture choice: we reuse the proven HustlePlaza shop primitives
 * (SmartProductCard, CartDrawer, ProductDetailModal, sm.* API helpers)
 * so cart/wishlist/orders flow through identical infrastructure.  The
 * only differences are visual chrome and the storefront filter on
 * product / search endpoints.
 *
 * Route: /tackle  (also rendered on tackle.au by SiteRouter)
 */
import { useEffect, useMemo, useState, useCallback, lazy, Suspense, Fragment } from "react";
import TackleAiChatWidget from "@/components/TackleAiChatWidget";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Loader2, Anchor, Fish, Waves, Ship } from "lucide-react";

import {
  api, sm, loadCart, saveCart,
} from "../components/plaza/plazaLib";
import SmartProductCard from "../components/plaza/SmartProductCard";
import ProductListTable from "../components/plaza/ProductListTable";
import PlazaPagination from "../components/plaza/PlazaPagination";

const ProductDetailModal = lazy(() =>
  import("../components/plaza/ProductDetailModal"));
const CartDrawer = lazy(() =>
  import("../components/plaza/CartDrawer"));

import TackleLogo from "../components/tackle/TackleLogo";
import CharterMegaMenu from "../components/tackle/CharterMegaMenu";
import PlazaAeo, { TACKLE_SITE } from "../components/plaza/PlazaAeo";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import "./TackleShopPage.css";

const STOREFRONT = "tackle";

// Same cart key as plaza — sharing a single cart across both shopfronts
// (per the user's brief: "products displayed in 2 storefronts, hustle
// and tackle").  Same SKUs → same line items → one checkout.
export default function TackleShopPage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [subcategories, setSubcategories] = useState([]);
  const [activeSub, setActiveSub] = useState(null);
  const [activeSku, setActiveSku] = useState(null);
  const [cart, setCart] = useState(loadCart());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerEverOpened, setDrawerEverOpened] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [shipping, setShipping] = useState({ enabled: true, threshold_cents: 9900 });
  const [wallet, setWallet] = useState({ balance_cents: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [charterQuery, setCharterQuery] = useState("");
  // View toggle — matches HustlePlazaShopPage UX (list is default so
  // fishos can eyeball 25+ SKUs at a glance instead of scrolling cards).
  const [view, setView] = useState("list");
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  // Pagination — matches HustlePlazaShopPage UX (25/50/100/250/500).
  const [gridSize, setGridSize] = useState(60);
  const [listSize, setListSize] = useState(90);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);                // list-mode 1-indexed page
  const pageLimit = view === "list" ? listSize : gridSize;
  // Real catalog counts from the materialised summary endpoint.
  const [summary, setSummary] = useState({ live: 0, total: 0 });
  const totalPages = Math.max(1, Math.ceil((summary.live || 0) / listSize));

  const goToCharters = useCallback((e) => {
    if (e) e.preventDefault();
    const q = charterQuery.trim();
    navigate(q ? `/fishing-tours?q=${encodeURIComponent(q)}` : "/fishing-tours");
  }, [charterQuery, navigate]);

  // Social-proof map: sku → {added, bought, viewing_recent}
  const [proofByS, setProofByS] = useState({});
  useEffect(() => {
    const skus = products.map((p) => p.sku).filter(Boolean);
    if (!skus.length) return;
    let cancelled = false;
    sm.socialProof(skus, 7).then((d) => {
      if (!cancelled) setProofByS(d.counts || {});
    });
    return () => { cancelled = true; };
  }, [products]);

  const charterShortcuts = useMemo(() => ([
    { label: "SEQ",            href: "/fishing-tours?q=Queensland" },
    { label: "Brisbane",       href: "/fishing-tours?region=moreton-bay" },
    { label: "Gold Coast",     href: "/fishing-tours?region=gold-coast" },
    { label: "Sunshine Coast", href: "/fishing-tours?region=sunshine-coast" },
    { label: "Noosa",          href: "/fishing-tours?region=noosa" },
    { label: "Hervey Bay",     href: "/fishing-tours?region=hervey-bay" },
    { label: "Cairns",         href: "/fishing-tours?region=cairns" },
    { label: "Darwin · Barra", href: "/fishing-tours?region=darwin" },
    { label: "Marlin",         href: "/fishing-tours?q=marlin" },
    { label: "Snapper",        href: "/fishing-tours?q=snapper" },
    { label: "Overseas",       href: "/fishing-tours?q=Norway+OR+Maldives+OR+Costa" },
  ]), []);

  // Initial load — tackle-filtered catalogue + sidebar subcategories.
  useEffect(() => {
    Promise.all([
      sm.listProducts({ availability: "live", storefront: STOREFRONT, limit: pageLimit }),
      sm.storefrontSubcategories(STOREFRONT),
      sm.freeShipping(),
      sm.wallet().catch(() => ({ balance_cents: 0 })),
      sm.wishlist().catch(() => []),
      sm.storefrontSummary(STOREFRONT).catch(() => ({ live: 0, total: 0, subcategories: [] })),
    ]).then(([prods, subs, ship, w, wl, sm_]) => {
      setProducts(prods);
      setSubcategories(subs);
      setShipping(ship);
      setWallet(w);
      setWishlist(wl);
      setSummary(sm_);
      setHasMore(prods.length >= pageLimit && prods.length < (sm_?.live || 0));
      setLoading(false);
    });
    const sku = searchParams.get("sku");
    if (sku) setActiveSku(sku);
     
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 320);
    return () => clearTimeout(t);
  }, [query]);

  // Re-fetch on filter change
  useEffect(() => {
    setLoading(true);
    if (debouncedQuery && debouncedQuery.length >= 2) {
      sm.fuzzySearch(debouncedQuery, null, STOREFRONT).then(d => {
        const list = (d.results || []).map(r => ({
          ...r,
          images: r.image_url ? [{ public_url: r.image_url }] : [],
          selling_price_cents: r.effective_price_cents || r.selling_price_cents,
        }));
        setProducts(list);
        setHasMore(false); // search results aren't paginated
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      // List mode uses numbered pagination (page/skip); grid mode
      // starts from 0 and appends via loadMore.
      const skip = view === "list" ? (page - 1) * listSize : 0;
      sm.listProducts({
        availability: "live",
        storefront: STOREFRONT,
        limit: pageLimit,
        skip,
        ...(activeSub ? { subcategory: activeSub } : {}),
      }).then(p => {
        setProducts(p);
        setHasMore(p.length >= pageLimit);
        setLoading(false);
      });
    }
  }, [debouncedQuery, activeSub, pageLimit, page, view, listSize]);

  // Reset page when filters change so we don't land on an empty page.
  useEffect(() => { setPage(1); }, [debouncedQuery, activeSub, listSize]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const more = await sm.listProducts({
        availability: "live",
        storefront: STOREFRONT,
        limit: gridSize,
        skip: products.length,
        ...(activeSub ? { subcategory: activeSub } : {}),
      });
      // De-dupe on SKU (defensive — server may return an item we
      // already have on the boundary).
      const seen = new Set(products.map((p) => p.sku));
      const fresh = more.filter((p) => !seen.has(p.sku));
      setProducts([...products, ...fresh]);
      setHasMore(more.length >= gridSize);
    } finally {
      setLoadingMore(false);
    }
  }, [products, hasMore, loadingMore, gridSize, activeSub]);

  useEffect(() => { saveCart(cart); }, [cart]);

  const subtotalCents = useMemo(
    () => cart.reduce((s, l) => s + (l.price_cents * l.qty), 0), [cart]);
  const cartCount = cart.reduce((n, l) => n + l.qty, 0);

  const addToCart = useCallback((product, qty = 1) => {
    setCart(prev => {
      const ix = prev.findIndex(l => l.sku === product.sku);
      if (ix >= 0) {
        const next = [...prev];
        next[ix] = { ...next[ix], qty: next[ix].qty + qty };
        return next;
      }
      return [...prev, {
        sku: product.sku,
        title: product.title,
        price_cents: product.selling_price_cents || product.rrp_cents || 0,
        image_url: (product.images || [{}])[0]?.public_url,
        availability: product.availability,
        restock_eta_iso: product.restock_eta_iso,
        days_until_restock: product.days_until_restock,
        qty,
      }];
    });
    sm.trackCart(product.sku, "add", qty);
    sm.signal({
      action: "add", sku: product.sku,
      category: product.category_path,
      cents: product.selling_price_cents || 0,
      meta: { storefront: STOREFRONT },
    });
    setDrawerEverOpened(true);
    setDrawerOpen(true);
  }, []);

  const removeLine = (sku) => {
    setCart(c => c.filter(l => l.sku !== sku));
    sm.trackCart(sku, "remove");
  };
  const updateQty = (sku, qty) => {
    if (qty <= 0) return removeLine(sku);
    setCart(c => c.map(l => l.sku === sku ? { ...l, qty } : l));
  };
  const clearCart = () => setCart([]);

  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const onCheckout = useCallback(async () => {
    if (!cart.length) return;
    setCheckoutBusy(true);
    cart.forEach(l => sm.trackCart(l.sku, "checkout_start", l.qty));
    try {
      const items = cart.map(l => ({
        sku: l.sku, qty: l.qty,
        cents: l.selling_price_cents || l.effective_price_cents || 0,
      }));
      const body = {
        items,
        shipping_address: {
          name: "Tackle Shopper",
          line1: "TBC at fulfilment",
          city: "Brisbane", state: "QLD",
          postcode: "4000", country: "AU",
        },
        freight_cents: 0,
        buyer_email: "checkout-tbd@tackle.au",
        wallet_redeem_cents: 0,
        storefront: STOREFRONT,
      };
      const r = await api.post(`/api/payments/checkout`, body);
      if (r.data.fully_paid_by_wallet) {
        clearCart();
        alert(`Order placed! ID: ${r.data.order_id}\nFully paid by Hustle Dollars`);
      } else {
        alert(
          `Order created: ${r.data.order_id}\n` +
          `Amount: $${(r.data.amount_cents/100).toFixed(2)}\n` +
          `Wallet applied: $${(r.data.wallet_applied_cents/100).toFixed(2)}`
        );
        clearCart();
      }
    } catch (e) {
      alert(`Checkout error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCheckoutBusy(false);
    }
  }, [cart]);

  const toggleWishlist = useCallback(async (sku) => {
    const res = await sm.wishlistToggle(sku);
    sm.wishlist().then(setWishlist);
    return res.saved;
  }, []);
  const inWishlist = (sku) => wishlist.some(w => w.sku === sku);

  const openProduct = (sku) => {
    setActiveSku(sku);
    setSearchParams({ sku });
    sm.trackView(sku);
  };
  const closeProduct = () => {
    setActiveSku(null);
    const next = new URLSearchParams(searchParams);
    next.delete("sku");
    setSearchParams(next);
  };

  const totalLabel = summary.live > 0
    ? t("tackle.browse_n_items", {
        defaultValue: "Browse {{n}} live tackle items",
        n: summary.live.toLocaleString("en-AU"),
      })
    : (products.length > 0
        ? t("tackle.n_items", { defaultValue: "{{n}} items", n: products.length })
        : t("common.loading", { defaultValue: "Loading…" }));

  return (
    <div className="tackle-shop" data-testid="tackle-shop-root">
      <PlatformHeader
        brand={{ name: "Tackle.au", to: "/tackle" }}
        showBookCta={false}
        showCart={false}
      />
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="tackle-topbar" data-testid="tackle-topbar">
        <a href="/tackle" className="tackle-topbar__brand" data-testid="tackle-logo-link">
          <TackleLogo />
          <span className="tackle-topbar__brandtext">
            Tackle<span className="tackle-topbar__dot">.</span>au
          </span>
        </a>
        <nav className="tackle-topbar__nav">
          <a href="#rods" onClick={(e) => { e.preventDefault(); setActiveSub("Rods"); }}>Rods</a>
          <a href="#reels" onClick={(e) => { e.preventDefault(); setActiveSub("Reels"); }}>Reels</a>
          <a href="#lures" onClick={(e) => { e.preventDefault(); setActiveSub("Lures & Baits"); }}>Lures</a>
          <a href="#tackle" onClick={(e) => { e.preventDefault(); setActiveSub("Tackle & Terminal"); }}>Tackle</a>
          <a href="#kayaks" onClick={(e) => { e.preventDefault(); setActiveSub("Kayaks & Boats"); }}>Kayaks</a>
          <a href="#boats" onClick={(e) => { e.preventDefault(); setActiveSub("Boating Accessories"); }}>Boating</a>
          <a href="/fish-map" data-testid="tackle-nav-fish-map">Fish Map</a>
          <CharterMegaMenu />
        </nav>
        <button
          className="tackle-topbar__cart"
          onClick={() => { setDrawerEverOpened(true); setDrawerOpen(true); }}
          data-testid="tackle-cart-button"
        >
          Cart <span className="tackle-cart-count">{cartCount}</span>
        </button>
      </header>

      {/* ── Hero — deep water gradient + headline ───────────────── */}
      <section className="tackle-hero" data-testid="tackle-hero">
        <div className="tackle-hero__waves" aria-hidden>
          <Waves size={140} className="tackle-hero__wave tackle-hero__wave--1" />
          <Waves size={120} className="tackle-hero__wave tackle-hero__wave--2" />
          <Waves size={160} className="tackle-hero__wave tackle-hero__wave--3" />
        </div>
        <div className="tackle-hero__copy">
          <span className="tackle-hero__eyebrow">
            <Anchor size={14} /> Powered by HustlePlaza · Free freight over ${(shipping.threshold_cents/100).toFixed(0)}
          </span>
          <h1 className="tackle-hero__title">
            Fish smarter.<br/>
            <span className="tackle-hero__title-accent">Tackle harder.</span>
          </h1>
          <p className="tackle-hero__sub">
            {totalLabel} of Australian-warehoused rods, reels, lures, kayaks and boating
            kit. Direct-from-the-warehouse pricing — no middleman, no waiting weeks.
          </p>
          <div className="tackle-hero__search" data-testid="tackle-search-wrap">
            <Search size={18} />
            <input
              placeholder="Search rods, reels, kayaks, lures…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="tackle-search-input"
            />
          </div>
        </div>
      </section>

      {/* ── Charter directory search — links into /fishing-tours ── */}
      <section className="tackle-charter-band" data-testid="tackle-charter-band">
        <div className="tackle-charter-band__inner">
          <div className="tackle-charter-band__copy">
            <span className="tackle-charter-band__eyebrow">
              <Ship size={14} /> Fishing Charter Directory · 50 operators
            </span>
            <h3 className="tackle-charter-band__title">
              Book the boat. We&apos;ll sort the gear.
            </h3>
            <p className="tackle-charter-band__sub">
              50 vetted operators — SEQ deep sea (24), Cairns black marlin, Darwin barra,
              NZ snapper, Florida sails, Norway cod &amp; more. No booking fees, no middleman.
            </p>
          </div>
          <form
            className="tackle-charter-band__form"
            onSubmit={goToCharters}
            data-testid="tackle-charter-form"
          >
            <div className="tackle-charter-band__field">
              <Search size={16} />
              <input
                placeholder="Search by region or species (e.g. Mooloolaba, marlin, barra)…"
                value={charterQuery}
                onChange={(e) => setCharterQuery(e.target.value)}
                data-testid="tackle-charter-search-input"
                aria-label="Search fishing charter directory"
              />
            </div>
            <button
              type="submit"
              className="tackle-charter-band__btn"
              data-testid="tackle-charter-search-button"
            >
              Find a Charter →
            </button>
          </form>
        </div>
        <div className="tackle-charter-band__chips" data-testid="tackle-charter-chips">
          <span className="tackle-charter-band__chiplabel">Jump to:</span>
          {charterShortcuts.map((s) => (
            <a
              key={s.label}
              href={s.href}
              className="tackle-charter-band__chip"
              data-testid={`tackle-charter-chip-${s.label.replace(/\W+/g,'-').toLowerCase()}`}
              onClick={(e) => { e.preventDefault(); navigate(s.href); }}
            >
              {s.label}
            </a>
          ))}
        </div>
      </section>

      {/* ── Subcategory pills ───────────────────────────────────── */}
      <div className="tackle-pills" data-testid="tackle-pills">
        <button
          className={`tackle-pill ${!activeSub ? "is-active" : ""}`}
          onClick={() => setActiveSub(null)}
          data-testid="tackle-pill-all"
        >
          <Fish size={14} /> {t("tackle.all_tackle", { defaultValue: "All Tackle" })}
        </button>
        {subcategories.map((s) => (
          <button
            key={s.subcategory}
            className={`tackle-pill ${activeSub === s.subcategory ? "is-active" : ""}`}
            onClick={() => setActiveSub(s.subcategory)}
            data-testid={`tackle-pill-${s.subcategory.replace(/\W+/g,'-').toLowerCase()}`}
          >
            {s.subcategory}
            <span className="tackle-pill__count">{s.count}</span>
          </button>
        ))}
      </div>

      {/* ── Catalogue grid ──────────────────────────────────────── */}
      <main className="tackle-main">
        <div className="tackle-main__header">
          <h2 className="tackle-main__title">
            {activeSub || (debouncedQuery
              ? t("tackle.results_for", { defaultValue: 'Results for "{{q}}"', q: debouncedQuery })
              : t("tackle.featured_tackle", { defaultValue: "Featured tackle" }))}
          </h2>
          <div className="tackle-main__search">
            <Search size={15} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("tackle.search_placeholder", {
                defaultValue: "Search rods, reels, kayaks, lures…",
              })}
              data-testid="tackle-inline-search"
            />
          </div>
          <div className="tackle-main__controls">
            <span className="tackle-main__count">
              {loading
                ? t("common.loading", { defaultValue: "Loading…" })
                : t("tackle.items_count", {
                    defaultValue: "{{count}} items",
                    count: products.length,
                  })}
            </span>
          </div>
        </div>

        {/* ── Top pagination bar: view toggle + page-size selector ── */}
        {!loading && products.length > 0 && (
          <PlazaPagination
            view={view}
            onView={setView}
            pageSize={pageLimit}
            onPageSize={(n) => view === "list" ? setListSize(n) : setGridSize(n)}
            pageSizeOptions={view === "list"
              ? [30, 60, 90, 180, 300]
              : [24, 60, 120, 240, 480]}
            shown={products.length}
            total={debouncedQuery ? products.length : (summary.live || products.length)}
            countLabel={
              <>{t("tackle.showing_of", {
                defaultValue: "Showing {{shown}} of {{total}}",
                shown: products.length.toLocaleString("en-AU"),
                total: (summary.live || products.length).toLocaleString("en-AU"),
              })}</>
            }
            page={page}
            totalPages={debouncedQuery ? 1 : totalPages}
            onPage={setPage}
            hasMore={!debouncedQuery && hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            labels={{
              show:       t("tackle.show",       { defaultValue: "Show" }),
              perPage:    t("tackle.per_page",   { defaultValue: "per page" }),
              listView:   t("tackle.list_view",  { defaultValue: "List" }),
              gridView:   t("tackle.grid_view",  { defaultValue: "Grid" }),
              loadMore:   t("tackle.load_more",  { defaultValue: "Load more" }),
              loading:    t("common.loading",    { defaultValue: "Loading…" }),
              firstTitle: t("tackle.first_page", { defaultValue: "First page" }),
              prevTitle:  t("tackle.prev_page",  { defaultValue: "Previous page" }),
              nextTitle:  t("tackle.next_page",  { defaultValue: "Next page" }),
              lastTitle:  t("tackle.last_page",  { defaultValue: "Last page" }),
              lastLabel:  t("tackle.last",       { defaultValue: "Last" }),
            }}
            testidPrefix="tackle-pagi"
          />
        )}

        {loading ? (
          <div className="tackle-loading" data-testid="tackle-loading">
            <Loader2 className="tackle-spin" size={32} />
            <p>{t("tackle.casting_line", { defaultValue: "Casting the line…" })}</p>
          </div>
        ) : products.length === 0 ? (
          <div className="tackle-empty" data-testid="tackle-empty">
            <p>{t("tackle.no_matches", { defaultValue: "No matches. Try another keyword or pick a different category." })}</p>
          </div>
        ) : view === "list" ? (
          <ProductListTable
            products={products}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={(col) => {
              if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
              else { setSortBy(col); setSortDir("asc"); }
            }}
            onOpen={openProduct}
            onAdd={(p) => addToCart(p)}
          />
        ) : (
          <div className="tackle-grid" data-testid="tackle-product-grid">
            {products.map((p, idx) => (
              <Fragment key={p.sku}>
                <SmartProductCard
                  product={p}
                  onOpen={() => openProduct(p.sku)}
                  onAdd={() => addToCart(p)}
                  inWishlist={inWishlist(p.sku)}
                  onToggleWishlist={() => toggleWishlist(p.sku)}
                  proof={proofByS[p.sku]}
                />
                {idx === 7 && (
                  <button
                    type="button"
                    className="tackle-grid__charter-tile"
                    onClick={() => navigate("/fishing-tours")}
                    data-testid="tackle-grid-charter-tile"
                  >
                    <Ship size={28} />
                    <span className="tackle-grid__charter-eyebrow">
                      Fishing Charter Directory
                    </span>
                    <span className="tackle-grid__charter-title">
                      Book a fishing trip with 50 vetted operators
                    </span>
                    <span className="tackle-grid__charter-sub">
                      SEQ · Cairns · Darwin · NZ · Worldwide — no booking fees
                    </span>
                    <span className="tackle-grid__charter-cta">
                      Browse charters →
                    </span>
                  </button>
                )}
              </Fragment>
            ))}
          </div>
        )}

        {/* ── Bottom pagination: numbered nav (list) or load-more (grid) ── */}
        {!loading && products.length > 0 && (
          <PlazaPagination
            view={view}
            onView={setView}
            showViewToggle={false}
            pageSize={pageLimit}
            onPageSize={(n) => view === "list" ? setListSize(n) : setGridSize(n)}
            pageSizeOptions={view === "list"
              ? [30, 60, 90, 180, 300]
              : [24, 60, 120, 240, 480]}
            shown={products.length}
            total={debouncedQuery ? products.length : (summary.live || products.length)}
            countLabel={
              <>{t("tackle.showing_of", {
                defaultValue: "Showing {{shown}} of {{total}}",
                shown: products.length.toLocaleString("en-AU"),
                total: (summary.live || products.length).toLocaleString("en-AU"),
              })}</>
            }
            page={page}
            totalPages={debouncedQuery ? 1 : totalPages}
            onPage={setPage}
            hasMore={!debouncedQuery && hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            labels={{
              show:       t("tackle.show",       { defaultValue: "Show" }),
              perPage:    t("tackle.per_page",   { defaultValue: "per page" }),
              listView:   t("tackle.list_view",  { defaultValue: "List" }),
              gridView:   t("tackle.grid_view",  { defaultValue: "Grid" }),
              loadMore:   t("tackle.load_more",  { defaultValue: "Load more" }),
              loading:    t("common.loading",    { defaultValue: "Loading…" }),
              firstTitle: t("tackle.first_page", { defaultValue: "First page" }),
              prevTitle:  t("tackle.prev_page",  { defaultValue: "Previous page" }),
              nextTitle:  t("tackle.next_page",  { defaultValue: "Next page" }),
              lastTitle:  t("tackle.last_page",  { defaultValue: "Last page" }),
              lastLabel:  t("tackle.last",       { defaultValue: "Last" }),
            }}
            testidPrefix="tackle-pagi-bottom"
          />
        )}
      </main>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="tackle-footer" data-testid="tackle-footer">
        <div className="tackle-footer__inner">
          <div>
            <TackleLogo />
            <p className="tackle-footer__strap">
              Tackle.au — a HustlePlaza shopfront. AU-warehoused fishing kit at
              wholesale prices, dropshipped same-day from Dropshipzone.
            </p>
          </div>
          <div className="tackle-footer__cols">
            <div>
              <h4>Shop</h4>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveSub("Rods"); }}>Rods</a>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveSub("Reels"); }}>Reels</a>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveSub("Lures & Baits"); }}>Lures & Baits</a>
              <a href="#" onClick={(e) => { e.preventDefault(); setActiveSub("Kayaks & Boats"); }}>Kayaks & Boats</a>
            </div>
            <div>
              <h4>About</h4>
              <a href="/plaza">HustlePlaza</a>
              <a href="/sales">Become a vendor</a>
              <a href="/group-terms">Terms</a>
            </div>
          </div>
        </div>
        <div className="tackle-footer__legal">
          © {new Date().getFullYear()} Tackle.au · whitelabel of HustlePlaza · ABN registered AU
        </div>
      </footer>

      {activeSku && (
        <Suspense fallback={null}>
          <ProductDetailModal
            sku={activeSku}
            onClose={closeProduct}
            onAdd={addToCart}
            inWishlist={inWishlist(activeSku)}
            onToggleWishlist={() => toggleWishlist(activeSku)}
            onSwitchSku={setActiveSku}
          />
        </Suspense>
      )}

      <PlazaAeo
        site={TACKLE_SITE}
        category={activeSub ? { name: activeSub, category_id: activeSub } : null}
        products={products}
        product={products.find((p) => p.sku === activeSku) || null}
      />

      {drawerEverOpened && (
        <Suspense fallback={null}>
          <CartDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            lines={cart}
            onUpdateQty={updateQty}
            onRemove={removeLine}
            onClear={clearCart}
            subtotalCents={subtotalCents}
            shipping={shipping}
            wallet={wallet}
            onCheckout={onCheckout}
            checkoutBusy={checkoutBusy}
          />
        </Suspense>
      )}
      <TackleAiChatWidget />
      <SuiteFooter />
    </div>
  );
}
